/**
 * GL Repository Implementation
 * Implements GlRepository, calls glsApi and maps DTOs to entities
 */

import { GlRepository } from "../../domain/repositories/GlRepository";
import {
  GlsResultEntity,
  GlOperationResult,
  AccountTypeGroupsEntity,
} from "../../domain/entities/GlEntity";
import { fetchGls, createGl, updateGl, deleteGl, getAccountTypeGroups } from "../api/glsApi";
import { GlMapper } from "../mappers/GlMapper";

export class GlRepositoryImpl implements GlRepository {
  async fetchGls(params: {
    limit: number;
    offset: number;
    search?: string;
    account_type?: string;
    management?: boolean;
  }): Promise<GlsResultEntity> {
    const response = await fetchGls(params);
    return GlMapper.toGlsResultEntity(
      response,
      params.limit,
      params.offset
    );
  }

  async createGl(payload: {
    account_name: string;
    account_type: string;
    parent_account_id?: string | null;
    invoice?: boolean;
    bill?: boolean;
    node_kind?: "ledger" | "group";
    request_comments?: string;
  }): Promise<GlOperationResult> {
    const response = await createGl(payload);
    const data = response.data ?? {};
    const requiresReview = Boolean(data.requires_review);
    const reviewId = data.review_id;
    const glMap = data.gl_map;
    return GlMapper.toCreateUpdateOperationResult(
      requiresReview,
      reviewId,
      glMap
    );
  }

  async updateGl(
    accountId: string,
    payload: {
      account_name?: string;
      account_type?: string;
      parent_account_id?: string | null;
      invoice?: boolean;
      bill?: boolean;
      node_kind?: "ledger" | "group";
      request_comments?: string;
    }
  ): Promise<GlOperationResult> {
    const response = await updateGl(accountId, payload);
    const data = response.data ?? {};
    const requiresReview = Boolean(data.requires_review);
    const reviewId = data.review_id;
    const glMap = data.gl_map;
    return GlMapper.toCreateUpdateOperationResult(
      requiresReview,
      reviewId,
      glMap
    );
  }

  async deleteGl(
    accountId: string,
    payload?: { request_comments?: string }
  ): Promise<GlOperationResult> {
    const response = await deleteGl(accountId, payload);
    const data = response.data ?? {};
    const requiresReview = Boolean(data.requires_review);
    const reviewId = data.review_id;
    return GlMapper.toDeleteOperationResult(requiresReview, reviewId, undefined);
  }

  async getAccountTypeGroups(): Promise<AccountTypeGroupsEntity> {
    const response = await getAccountTypeGroups();
    return GlMapper.toAccountTypeGroupsEntity(response);
  }
}
